// Per-environment runtime config for the ENE.v2 frontend. Single tenant
// (energis), so no tenant dispatch needed — only environment selection.
//
// Der Host, der die SPA *ausliefert* (NICHT die Kunden-Website, von der
// aus per cos_parent.js hierher redirectet wird), bestimmt das Environment:
//   web-energisnetz-cos.dev.local             → DEV (lokaler Vite-Devserver)
//   ablesung-test.energis-netzgesellschaft.de → TEST
//   ablesung.energis-netzgesellschaft.de      → PROD
//   web-[test-]cos-energisnetz.prhos.com      → TEST/PROD (Alt-Domains; bleiben
//                                               während der Umstellung 08/2026
//                                               parallel gültig)
//
// Hinweis: www.energis-netzgesellschaft.de ist die Kunden-Website, die nur
// cos_parent_loader.js einbindet und die Nutzer auf die Standalone-SPA
// weiterleitet (kein Iframe mehr seit 07/2026). Diese Domain sieht die SPA
// **nicht** als eigenen window.location.hostname und ist hier folglich
// irrelevant.
//
// Config values (apiHost / odataBasePath / anylineLicenseKey) leben NICHT
// im Code, sondern in `public/cos-config.json`. Vite kopiert die Datei beim
// Build 1:1 nach `dist/cos-config.json`. Im Deployment wird sie wie die
// `appsettings.{HOSTNAME}.json` der API gehalten: Rollouts überschreiben
// sie nicht. So lässt sich z. B. ein neuer Anyline-Key ohne Rebuild
// austauschen — JSON tauschen, IIS-Reload nicht nötig, Browser-Reload
// reicht. Dasselbe gilt für die Wartungsmeldung (Block `maintenance`).

import { parseMaintenance, type MaintenanceConfig } from "./maintenance";

export interface EneConfig {
  apiHost: string;
  /** Base path for the anonymous OData proxy on the API. */
  odataBasePath: string;
  /** Base64-encoded Anyline Web SDK license key. Domain-bound — empty
   *  string disables the OCR-Scan-Button on this environment. */
  anylineLicenseKey: string;
  /** Optionale Wartungsmeldung, siehe maintenance.ts. */
  maintenance?: MaintenanceConfig;
}

/** The three deployment environments. Anything not explicitly PROD/TEST is
 *  treated as DEV (local dev hosts + any unknown host). */
export type EneEnv = "DEV" | "TEST" | "PROD";

export function resolveEnv(host: string = window.location.hostname): EneEnv {
  if (host === "ablesung.energis-netzgesellschaft.de") return "PROD";
  if (host === "ablesung-test.energis-netzgesellschaft.de") return "TEST";
  // Alt-Domains (Umstellung 08/2026): bleiben gültig, solange die prhos.com-
  // Bindings existieren — NICHT entfernen, bevor der IIS-seitige Redirect
  // alt→neu abgeschaltet ist, sonst liefe die Alt-Domain als DEV.
  if (host === "web-cos-energisnetz.prhos.com") return "PROD";
  if (host === "web-test-cos-energisnetz.prhos.com") return "TEST";
  return "DEV";
}

/** True only on the live production host. Used to gate dev-only affordances
 *  (e.g. the Dev-Mode switch) so they never surface to real customers. */
export function isProdEnv(host: string = window.location.hostname): boolean {
  return resolveEnv(host) === "PROD";
}

/**
 * Modul-lokaler Cache der geladenen Config. Wird beim App-Start von
 * `loadCosConfig` einmalig befüllt; danach liefert `resolveConfig`
 * synchron daraus. Vor dem Laden ist `null` — `resolveConfig` wirft
 * dann eine klare Diagnose-Meldung, statt still `undefined` zurückzugeben.
 */
let loadedConfig: Record<EneEnv, EneConfig> | null = null;

/**
 * Lädt die Runtime-Config einmalig. Im Browser fetcht sie
 * `../cos-config.json` (relativ zur SPA-`index.html`; landet in der
 * URL-Resolution unter dem Site-Root). Tests rufen sie mit `seed` auf
 * und umgehen damit Netz-IO.
 *
 * Idempotent: erneuter Aufruf nach erfolgreichem Laden ist ein No-Op
 * (außer ein Seed wird explizit übergeben — dann ersetzt der Seed den
 * Cache; nützlich für Test-Isolation).
 */
export async function loadCosConfig(
  seed?: Record<EneEnv, EneConfig>
): Promise<void> {
  if (seed) {
    loadedConfig = seed;
    return;
  }
  if (loadedConfig) return;

  // `cache: 'no-store'` umgeht den Browser-Cache, damit ein neuer Key
  // sofort nach Datei-Tausch wirksam wird (zusätzlich zu der no-cache-
  // Empfehlung in den IIS-Response-Headers). `import.meta.env.BASE_URL`
  // respektiert ein eventuelles Vite-`base`-Setup; aktuell ist das "/",
  // sodass die fertige URL "/cos-config.json" lautet.
  const url = `${import.meta.env.BASE_URL}cos-config.json`;
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(
      `[ene] cos-config.json konnte nicht geladen werden ` +
        `(${res.status} ${res.statusText}). Liegt die Datei unter ${url}?`
    );
  }
  const raw = (await res.json()) as Record<string, unknown>;
  const pick = (k: EneEnv): EneConfig => {
    const env = raw[k] as Partial<EneConfig> | undefined;
    if (
      !env ||
      typeof env.apiHost !== "string" ||
      typeof env.odataBasePath !== "string" ||
      typeof env.anylineLicenseKey !== "string"
    ) {
      throw new Error(
        `[ene] cos-config.json: Sektion "${k}" fehlt oder ist unvollständig.`
      );
    }
    return {
      apiHost: env.apiHost,
      odataBasePath: env.odataBasePath,
      anylineLicenseKey: env.anylineLicenseKey,
      maintenance: parseMaintenance(
        (env as Record<string, unknown>).maintenance,
        k
      ),
    };
  };
  loadedConfig = { DEV: pick("DEV"), TEST: pick("TEST"), PROD: pick("PROD") };
}

export function resolveConfig(host: string = window.location.hostname): EneConfig {
  if (!loadedConfig) {
    throw new Error(
      `[ene] resolveConfig() called before loadCosConfig() — ` +
        `main.ts muss vor dem app.mount() awaitn.`
    );
  }
  return loadedConfig[resolveEnv(host)];
}

/**
 * True when the SPA is running under a local development hostname. Used by
 * submitReading() to short-circuit the final SaveSet POST so test PINs don't
 * get burned on dev. ValidateSet still runs — only the destructive write
 * is blocked.
 *
 * The public Q environment (ablesung-test.energis-netzgesellschaft.de,
 * Alt-Domain web-test-cos-energisnetz.prhos.com) is NOT treated as
 * "dev" — that is an actual deployment and writes are real.
 */
/**
 * Customer-facing URLs that the iframe links to (Datenschutz, BFSG-
 * Erklärung). Centralised so a tenant rename touches one place.
 *
 * `datenschutz` points at the customer's public site (we don't host the
 * privacy policy ourselves).
 *
 * `bfsg` is a relative URL pointing at the landpages SPA's own BFSG
 * view; it resolves against whichever origin the meterreading SPA is
 * served from (DEV/TEST/PROD), so target="_blank" opens our own BFSG
 * page in a new tab without going through the customer-CMS wrapping.
 */
export const customerLinks = {
  datenschutz: "https://www.energis-netzgesellschaft.de/datenschutz",
  bfsg: "/landpages/index.html?type=Bfsg",
} as const;

export function isDevHost(host: string = window.location.hostname): boolean {
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host.endsWith(".dev.local") ||
    host.endsWith(".local")
  );
}

/**
 * True on touch devices (smartphones, tablets). Combines a CSS media-query
 * check (`pointer: coarse`) — the most reliable runtime indicator that the
 * primary input is a finger rather than a mouse — with a coarse User-Agent
 * fallback for browsers that report `pointer: fine` despite being mobile
 * (some Android tablet browsers in desktop-mode).
 */
export function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;
  const coarse =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  const ua = navigator.userAgent || "";
  return coarse || /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
}

/**
 * True when the OCR-Scan-Button should be visible in the *normal* (device-
 * appropriate) view:
 *   - we are on a touch device, AND
 *   - the current environment has an Anyline license key configured.
 *
 * Without a key the SDK won't initialise, so we hide the button rather than
 * fail at the click. Kept synchronous + side-effect-free so it can be used in
 * Vue template `v-if` / `:disabled` bindings.
 *
 * Desktop testing of the scan flow (incl. on local dev) is handled by the
 * Dev-Mode switch (see useDevMode), NOT by host sniffing — so this no longer
 * special-cases `.local` hosts.
 */
export function isAnylineButtonEligible(): boolean {
  return isMobileDevice() && !!resolveConfig().anylineLicenseKey;
}

/**
 * Diagnostic aid. Logs a console warning when the OCR-Scan-Button is suppressed
 * *purely* because the current environment has no Anyline license key — even
 * though we're on a touch device. This is exactly the situation that looks like
 * "mobile is not detected" but is really a missing key (see
 * [[isAnylineButtonEligible]]). Call once at SPA / view bootstrap.
 *
 * `mobile` is injectable so the behaviour is testable without faking the
 * matchMedia / userAgent probes; it defaults to the live isMobileDevice().
 */
export function warnIfScanSuppressedByMissingKey(
  host: string = window.location.hostname,
  mobile: boolean = isMobileDevice()
): void {
  if (mobile && !resolveConfig(host).anylineLicenseKey) {
    console.warn(
      `[ene] OCR-Scan-Button hidden on a touch device because no Anyline ` +
        `license key is configured for host "${host}". This is a config ` +
        `issue, NOT a mobile-detection problem — set anylineLicenseKey for ` +
        `this environment in shared/config/ene.ts.`
    );
  }
}
